<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide">
    <q-card
      :style="cardStyle"
      class="relative-position"
      :class="{ 'overflow-hidden': illustration }"
    >
      <PDialogHeader
        v-if="!illustration"
        :title="title"
        :closable="closable"
        :header-class="headerClass"
        @close="onClose"
      />

      <q-card-section
        v-else
        class="column items-center text-center q-pt-2x-xxl q-px-2x-md q-pb-2x-lg"
      >
        <q-icon size="80px" class="q-mb-2x-md">
          <component :is="illustration" />
        </q-icon>
        <div class="text-lg text-weight-regular text-neutral">
          {{ title }}
        </div>
      </q-card-section>

      <q-card-section v-if="table">
        <PMarkupTable :columns="table.columns" :rows="table.data" />
      </q-card-section>

      <q-card-section v-if="label || text || htmlContent" :class="labelClass">
        <span v-if="htmlContent" v-html="htmlContent"></span>

        <span class="text-md text-neutral text-weight-medium q-px-md">
          {{ text }}
        </span>

        <p
          v-if="label"
          class="text-md text-neutral-80 text-weight-regular q-px-md"
        >
          {{ label }}
        </p>
      </q-card-section>

      <footer
        v-if="isFooterVisible"
        class="rounded-borders text-neutral-90 text-center"
      >
        <div :class="footerClass">
          <PBtn
            v-if="cancellable"
            :fluid="Boolean(illustration)"
            :label="cancelLabel"
            :style="cancelBtnStyle"
            :class="{
              'col text-lg text-weight-regular q-py-sm no-border-radius':
                illustration,
            }"
            :color="illustration ? 'neutral-10' : undefined"
            :text-color="illustration ? 'primary' : undefined"
            type="cancel"
            :outline="!illustration"
            @click="onCancel"
          />

          <PBtn
            v-if="submittable"
            :fluid="Boolean(illustration)"
            :loading="loading"
            :label="submitLabel"
            :style="submitBtnStyle"
            :class="{
              'col text-lg text-weight-regular q-py-sm no-border-radius':
                illustration,
            }"
            type="submit"
            @click="onSubmit"
          />
        </div>
      </footer>
    </q-card>
  </q-dialog>
</template>

<script setup>
import { useDialogPluginComponent } from "quasar";

const emit = defineEmits([
  ...useDialogPluginComponent.emits,
  "confirm",
  "close",
  "cancel",
]);

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } =
  useDialogPluginComponent();

defineProps({
  title: {
    type: String,
    default: "",
  },
  text: {
    type: String,
    default: "",
  },
  htmlContent: {
    type: String,
    default: "",
  },
  label: {
    type: String,
    default: "",
  },
  isFooterVisible: {
    type: Boolean,
    default: true,
  },
  cancellable: {
    type: Boolean,
    default: true,
  },
  cancelLabel: {
    type: String,
    default: "No",
  },
  submitLabel: {
    type: String,
    default: "Yes",
  },
  submittable: {
    type: Boolean,
    default: true,
  },
  loading: {
    type: Object,
    default: () => {},
  },
  closable: {
    type: Boolean,
    default: true,
  },
  labelClass: {
    type: String,
    default: "text-center q-ma-xl bg-surface rounded-borders",
  },
  table: {
    type: Object,
    default: () => {},
  },
  cardStyle: {
    type: Object,
    default: () => {
      return { minWidth: "auto" };
    },
  },
  submitBtnStyle: {
    type: Object,
    default: () => {
      return { marginInlineEnd: "3rem" };
    },
  },
  cancelBtnStyle: {
    type: Object,
    default: () => {
      return { marginInline: "3rem 1rem" };
    },
  },
  footerClass: {
    type: String,
    default: "q-py-xxl row items-center justify-end",
  },
  illustration: {
    type: [Object, Function, String],
    default: null,
  },
  headerClass: {
    type: String,
    default: "q-px-2x-md q-py-xl flex justify-between items-center",
  },
});

const onClose = async () => {
  emit("close");

  onDialogHide();
};

const onCancel = async () => {
  emit("cancel");

  onDialogCancel();
};

const onSubmit = async () => {
  emit("confirm");

  onDialogOK();
};
</script>
